// Compiles the guided StoryModel into a real ink script, then into the JSON
// the inkjs runtime consumes. Also derives the engine-facing view of the
// story (floors, scene colors, toasts) so the game never reads the model
// shape directly.
//
// Works in the browser (for live preview in the story editor) and in Bun
// (for offline verification).

import { Compiler } from "inkjs/compiler/Compiler";
import type { FloorNode } from "@/roguelike/floorPool";
import type { SceneChoice, StoryModel, StoryScene, VarValue } from "./model";

export interface CompileReport {
  ink: string;
  json: string;
  errors: string[];
  warnings: string[];
  floors: FloorNode[];
  sceneColors: Record<string, string>;
  messages: Record<string, string>;
  startScene: StoryScene | null;
}

const GATE_SCENE_KEY = "garden";

export function compileStory(model: StoryModel): CompileReport {
  const messages: Record<string, string> = {
    boot: "COMMENCE",
    ...(model.messages ?? {}),
  };
  const sceneColors: Record<string, string> = {};
  for (const scene of model.scenes ?? []) {
    if (scene.color) sceneColors[scene.sceneKey || scene.id] = scene.color;
  }
  if (model.schema === "roguelike" && !sceneColors[GATE_SCENE_KEY]) {
    sceneColors[GATE_SCENE_KEY] = "#0c1e2a";
  }
  const floors: FloorNode[] =
    model.schema === "roguelike"
      ? (model.scenes ?? [])
          .filter((s) => s.kind === "floor")
          .map((s) => ({
            path: s.id,
            name: s.title || s.id,
            tier: s.tier as 1 | 2 | 3,
          }))
      : [];
  const startScene =
    (model.scenes ?? []).find((s) => s.id === model.start) ?? null;

  const ink = renderInk(model);
  const errors: string[] = [];
  const warnings: string[] = validateLines(model);

  let json = "";
  if (ink.trim().length > 0) {
    const compiler = new Compiler(ink);
    try {
      const story = compiler.Compile();
      errors.push(...compiler.errors.map(String));
      warnings.push(...compiler.warnings.map(String));
      if (errors.length === 0 && story) {
        const toJson = story.ToJson();
        json = typeof toJson === "string" ? toJson : JSON.stringify(toJson);
      }
    } catch (err) {
      errors.push(err instanceof Error ? err.message : String(err));
      warnings.push(...compiler.warnings.map(String));
    }
  }

  return {
    ink,
    json,
    errors,
    warnings,
    floors,
    sceneColors,
    messages,
    startScene,
  };
}

export function renderInk(model: StoryModel): string {
  const lines: string[] = [];
  lines.push("// Generated by the DevAdventure2K story editor");
  lines.push(`// schema: ${model.schema}`);
  lines.push("");

  const variables = effectiveVariables(model);
  for (const [name, value] of Object.entries(variables)) {
    lines.push(`VAR ${name} = ${formatValue(value)}`);
  }
  if (Object.keys(variables).length > 0) lines.push("");

  if (model.schema === "roguelike") {
    const startId = model.start || "start";
    lines.push(`-> ${startId}`);
    lines.push("");
    lines.push(renderStartKnot(model, startId));
    lines.push(renderGate());
    for (const scene of model.scenes ?? []) {
      if (scene.id === startId) continue;
      lines.push("");
      lines.push(renderScene(scene));
    }
  } else {
    lines.push(`-> ${model.start || "start"}`);
    lines.push("");
    const scenes = model.scenes ?? [];
    for (const scene of scenes) {
      lines.push("");
      lines.push(renderScene(scene));
    }
    if (scenes.length === 0) {
      lines.push("=== start ===");
      lines.push("A short, empty story. Add scenes in the editor.");
      lines.push("-> DONE");
    }
  }
  lines.push("");

  return lines.join("\n");
}

function effectiveVariables(model: StoryModel): Record<string, VarValue> {
  const vars: Record<string, VarValue> = { ...(model.variables ?? {}) };
  if (model.schema === "roguelike") {
    if (!("health" in vars)) vars.health = 100;
    if (!("floor_number" in vars)) vars.floor_number = 1;
    if (!("tier" in vars)) vars.tier = 1;
  }
  return vars;
}

function formatValue(value: VarValue): string {
  if (typeof value === "string") return `"${value.replace(/"/g, '\\"')}"`;
  return String(value);
}

function renderStartKnot(model: StoryModel, startId: string): string {
  const start = (model.scenes ?? []).find((s) => s.id === startId);
  const out: string[] = [`=== ${startId} ===`];
  if (start) {
    out.push(`# SCENE::${start.sceneKey || start.id}`);
    for (const tag of start.tags ?? []) out.push(`# ${tag}`);
    for (const line of start.lines ?? []) out.push(line);
  } else {
    out.push("# SCENE::desk");
    out.push("Your story begins.");
  }
  out.push("-> floor_gate");
  return out.join("\n");
}

function renderGate(): string {
  return [
    "=== floor_gate ===",
    "~ floor_number += 1",
    `# SCENE::${GATE_SCENE_KEY}`,
    "# NEXT_FLOOR::request",
    "The maintenance elevator only goes down. Floor {floor_number}.",
    "-> DONE",
  ].join("\n");
}

function renderScene(scene: StoryScene): string {
  const out: string[] = [`=== ${scene.id} ===`];
  out.push(`# SCENE::${scene.sceneKey || scene.id}`);
  for (const tag of scene.tags ?? []) out.push(`# ${tag}`);

  for (const line of scene.lines ?? []) out.push(line);

  if (scene.kind === "floor" && (scene.choices ?? []).length === 0) {
    out.push("This floor is empty. Add choices in the editor.");
  }

  const choices = scene.choices ?? [];
  if (choices.length === 0) {
    out.push("-> DONE");
  } else {
    for (const choice of choices) {
      out.push("");
      out.push(renderChoice(choice));
    }
  }
  return out.join("\n");
}

function renderChoice(choice: SceneChoice): string {
  const out: string[] = [];
  const label = choice.label || "(unlabeled)";
  const header =
    choice.condition && choice.condition.trim()
      ? `+ {${choice.condition.trim()}} [${label}]`
      : `+ [${label}]`;
  out.push(header);

  const effects = renderEffects(choice);
  for (const effect of effects) out.push(effect);

  for (const line of choice.lines ?? []) out.push(line);
  for (const tag of choice.tags ?? []) out.push(`# ${tag}`);

  out.push(`-> ${choice.target || "DONE"}`);
  return out.join("\n");
}

function renderEffects(choice: SceneChoice): string[] {
  const effects: string[] = [];
  for (const [name, value] of Object.entries(choice.set ?? {})) {
    if (!name) continue;
    effects.push(`~ ${name} = ${formatValue(value)}`);
  }
  for (const [name, value] of Object.entries(choice.add ?? {})) {
    if (!name) continue;
    if (typeof value === "number" && value < 0) {
      effects.push(`~ ${name} -= ${Math.abs(value)}`);
    } else if (value !== 0) {
      effects.push(`~ ${name} += ${formatValue(value)}`);
    }
  }
  return effects;
}

function validateLines(model: StoryModel): string[] {
  const warnings: string[] = [];
  const suspicious = ["*", "+", "-", ">", "~", "#", "=", "{", "}", "->"];
  const visit = (where: string, lines: string[]): void => {
    (lines ?? []).forEach((line) => {
      const trimmed = line.trimStart();
      if (trimmed.length === 0) return;
      for (const marker of suspicious) {
        if (trimmed.startsWith(marker)) {
          warnings.push(
            `"${line}" (${where}) starts with ink syntax ("${marker}") and may render oddly. Rephrase it.`
          );
          break;
        }
      }
    });
  };
  for (const scene of model.scenes ?? []) {
    visit(`scene ${scene.id}`, scene.lines);
    for (const choice of scene.choices ?? []) {
      visit(`choice "${choice.label}"`, choice.lines);
    }
  }
  return warnings;
}
